import React, { useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { message, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { RcFile, UploadProps } from 'antd/es/upload';
import request from '@/api/network';
import { state } from '@/store';
import styles from './index.module.scss';

type ImageUploaderProps = {
  value?: any;
  onChange?: (val?: any) => void;
  disabled?: boolean;
  formats?: string[];
  maxSizeKB?: number;
  limitWidth?: number;
  limitHeight?: number;
  width?: number;
  height?: number;
  tip?: string;
  sync?: number | boolean;
  crop?: boolean;
  cropAspect?: number;
  outputWidth?: number;
  outputHeight?: number;
  showTip?: boolean;
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onChange,
  disabled,
  maxSizeKB,
  formats,
  limitWidth,
  limitHeight,
  width = 80,
  height = 80,
  tip,
  sync,
  crop = false,
  cropAspect,
  outputWidth,
  outputHeight,
  showTip = true,
}) => {
  const [uploading, setUploading] = useState(false);
  const allowedFormats = (formats && formats.length ? formats : ['jpg', 'png', 'jpeg']).map(item =>
    String(item).toLowerCase(),
  );
  const formatText = allowedFormats.join('、');

  const resolveUrl = (val?: any) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val?.imageUrl || val?.tmUrl || val?.jdUrl || val?.tmImageUrl || val?.jdImageUrl || '';
  };

  const valueSignature = (() => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  })();

  const [previewUrl, setPreviewUrl] = useState<string>(() => resolveUrl(value));

  useEffect(() => {
    setPreviewUrl(resolveUrl(value));
  }, [valueSignature]);

  const validateFile = (file: File) =>
    new Promise<void>((resolve, reject) => {
      const name = file.name || '';
      const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() : '';
      const type = (file.type || '').toLowerCase();
      const matchedByType = allowedFormats.some(fmt => type.includes(`image/${fmt === 'jpg' ? 'jpeg' : fmt}`));
      const matchedByExt = ext ? allowedFormats.includes(ext) : false;
      if (!matchedByType && !matchedByExt) {
        reject(new Error(`图片格式仅支持 ${formatText}`));
        return;
      }
      if (maxSizeKB && file.size / 1024 > maxSizeKB) {
        reject(new Error(`图片大小需小于 ${maxSizeKB}KB`));
        return;
      }
      if (limitWidth || limitHeight) {
        const img = new Image();
        img.onload = () => {
          if (limitWidth && img.width !== limitWidth) {
            reject(new Error(`图片宽度需为 ${limitWidth}px`));
            return;
          }
          if (limitHeight && img.height !== limitHeight) {
            reject(new Error(`图片高度需为 ${limitHeight}px`));
            return;
          }
          resolve();
        };
        img.onerror = () => reject(new Error('图片校验失败'));
        img.src = URL.createObjectURL(file);
      } else {
        resolve();
      }
    });

  const resizeImage = (file: File, targetWidth: number, targetHeight: number) =>
    new Promise<File>((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('Canvas 不支持'));
          return;
        }
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob(blob => {
          URL.revokeObjectURL(url);
          if (!blob) {
            reject(new Error('生成裁剪图片失败'));
            return;
          }
          const nextFile = new File([blob], file.name, {
            type: file.type || 'image/png',
            lastModified: Date.now(),
          });
          resolve(nextFile);
        }, file.type || 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('图片加载失败'));
      };
      img.src = url;
    });

  const handleUpload = async (rawFile: RcFile | File) => {
    let file: File = rawFile as File;
    if (outputWidth && outputHeight) {
      file = await resizeImage(file, outputWidth, outputHeight);
      const resizedSizeKB = (file.size / 1024).toFixed(2);
      // 打印裁剪后图片的尺寸和大小
      // eslint-disable-next-line no-console
      console.log(`[ImageUploader] resized to ${outputWidth}x${outputHeight}, size ${resizedSizeKB}KB`);
    }
    await validateFile(file);
    const formData = new FormData();
    formData.append('file', file);
    if (state.currentShopId) formData.append('shopId', String(state.currentShopId));
    if (state.currentShopPlatformType) formData.append('platform', String(state.currentShopPlatformType));
    if (sync) formData.append('sync', '1');
    setUploading(true);
    try {
      const res: any = await request({
        url: '/member/royalcanin/applet/image/uploadImage',
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const jdImageUrl = res?.data?.jdImageUrl;
      const tmImageUrl = res?.data?.tmImageUrl;
      const imageUrl = res?.data?.imageUrl || tmImageUrl || jdImageUrl;
      if (imageUrl || jdImageUrl || tmImageUrl) {
        setPreviewUrl(imageUrl || tmImageUrl || jdImageUrl || '');
        if (sync) {
          onChange?.({
            jdUrl: jdImageUrl,
            tmUrl: tmImageUrl,
            imageUrl,
          });
        } else {
          onChange?.(imageUrl);
        }
        message.success('上传成功');
      }
    } finally {
      setUploading(false);
    }
  };

  const uploadContent = (
    <div
      className={styles.box}
      style={{ width, height, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="thumb"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <PlusOutlined style={{ fontSize: 20, color: '#999' }} />
      )}
    </div>
  );

  const uploadElement = (
    <Upload
      accept={allowedFormats.map(fmt => `.${fmt}`).join(',')}
      showUploadList={false}
      customRequest={async (options: Parameters<NonNullable<UploadProps['customRequest']>>[0]) => {
        const file = options.file as RcFile;
        try {
          await handleUpload(file);
          options.onSuccess?.({}, file);
        } catch (err: any) {
          if (err instanceof Error && err.message) {
            message.error(err.message);
          }
          options.onError?.(err as any);
        }
      }}
      disabled={disabled || uploading}>
      {uploadContent}
    </Upload>
  );

  return (
    <div className={styles.uploader}>
      {crop ? (
        <ImgCrop
          rotationSlider
          aspect={cropAspect}
          modalOk="裁剪"
          modalCancel="取消">
          {uploadElement}
        </ImgCrop>
      ) : (
        uploadElement
      )}
      {showTip && (tip || maxSizeKB || limitWidth || limitHeight) && (
        <div className={styles.tip}>
          {tip ||
            `请上传一张图片尺寸为${limitWidth || '任意'} x ${limitHeight || '任意'}像素${
              maxSizeKB ? `且小于${maxSizeKB}KB` : ''
            }的 ${formatText}`}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
